import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';

const Orientations = () => {
  const [orientations, setOrientations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrientation, setSelectedOrientation] = useState(null);

  useEffect(() => {
    fetchOrientations();
  }, []);

  const fetchOrientations = async () => {
    try {
      // const response = await fetch('/admin/api/orientations.php');
      const response = await fetch('http://localhost:8000/api/orientations.php');
      const data = await response.json();
      setOrientations(data);
    } catch (error) {
      console.error('Error fetching orientations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Orientation Sessions</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Welcome to our comprehensive orientation programs designed to help new students and parents 
              understand our school culture, policies, and academic expectations.
            </p>
          </div>
        </div>
      </div>

      {/* Orientations Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {orientations.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orientations Available</h3>
              <p className="text-gray-600">Check back later for upcoming orientation sessions.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {orientations.map((orientation) => (
              <div
                key={orientation.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                  {orientation.image_path && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={`admin/${orientation.image_path}`}
                        alt={orientation.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      orientation.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {orientation.status === 'active' ? 'Active' : 'Upcoming'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {orientation.duration} minutes
                    </span>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {orientation.name}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-calendar-alt w-4 h-4 mr-2"></i>
                      <span className="text-sm">{formatDate(orientation.date)}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-clock w-4 h-4 mr-2"></i>
                      <span className="text-sm">{formatTime(orientation.time)}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-map-marker-alt w-4 h-4 mr-2"></i>
                      <span className="text-sm">{orientation.location}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-users w-4 h-4 mr-2"></i>
                      <span className="text-sm">{orientation.audience}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {orientation.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      <i className="fas fa-user-tie mr-1"></i>
                      {orientation.instructor}
                    </div>
                    
                    <button
                      onClick={() => setSelectedOrientation(orientation)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for Orientation Details */}
      {selectedOrientation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedOrientation.name}
                </h2>
                <button
                  onClick={() => setSelectedOrientation(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              {selectedOrientation.image_path && (
                <div className="mb-6">
                  <img
                    src={`admin/${selectedOrientation.image_path}`}
                    alt={selectedOrientation.name}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Date & Time</h4>
                  <p className="text-gray-600">
                    {formatDate(selectedOrientation.date)} at {formatTime(selectedOrientation.time)}
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Location</h4>
                  <p className="text-gray-600">{selectedOrientation.location}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Audience</h4>
                  <p className="text-gray-600">{selectedOrientation.audience}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Duration</h4>
                  <p className="text-gray-600">{selectedOrientation.duration} minutes</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                <p className="text-gray-600 leading-relaxed">
                  {selectedOrientation.description}
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Instructor</h4>
                <p className="text-gray-600">{selectedOrientation.instructor}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Orientations;
